import axios from "axios";
import store from "../store/store";
import { getAccessToken, logoutUser } from "../store/login";

// Create Axios instance
const useJWTInterceptors = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

// Queue to hold requests while refreshing
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor
useJWTInterceptors.interceptors.request.use(
  (config) => {
    const state = store.getState().login;
    const token = state.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
useJWTInterceptors.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        // If a refresh is already in progress, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return useJWTInterceptors(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const resultAction = await store.dispatch(getAccessToken());

        if (getAccessToken.rejected.match(resultAction)) {
          // Refresh failed → log out
          store.dispatch(logoutUser());
          processQueue(resultAction.payload || "Refresh token invalid", null);
          window.location.href = "/login"; // optional redirect
          return Promise.reject(
            resultAction.payload || "Refresh token invalid"
          );
        }

        const state = store.getState().login;
        const newToken = state.accessToken;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processQueue(null, newToken);
        return useJWTInterceptors(originalRequest);
      } catch (err) {
        store.dispatch(logoutUser());
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default useJWTInterceptors;
